import { useMemo } from "react";

const STOP_WORDS = new Set(
  (
    "a an and are as at be been but by for from had has have he her his i if in into is it its me my " +
    "not of on or our she so than that the their them they this to was we were what when which who " +
    "will with would you your very also can could do does did just more most much only some such too " +
    "out really comes come through plus about there here all any both each how other over then these " +
    "those well where why being get got one two"
  ).split(" ")
);

export interface WordCount {
  word: string;
  count: number;
}

// Lowercases, strips punctuation and stop words, and returns the most frequent words.
export function countWords(texts: string[], limit = 40): WordCount[] {
  const counts = new Map<string, number>();
  for (const text of texts) {
    for (const raw of text.toLowerCase().split(/[^a-z0-9']+/)) {
      const word = raw.replace(/^'+|'+$/g, "");
      if (word.length < 3 || STOP_WORDS.has(word)) continue;
      counts.set(word, (counts.get(word) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count || a.word.localeCompare(b.word))
    .slice(0, limit);
}

export interface PlacedWord extends WordCount {
  size: number;
  x: number;
  y: number;
  w: number;
  h: number;
  tier: number;
}

type Measure = (word: string, size: number) => number;

// Rough width for a sans-serif face when no canvas is available (tests, SSR).
const estimateWidth: Measure = (word, size) => word.length * size * 0.56;

let canvasCtx: CanvasRenderingContext2D | null | undefined;
function browserMeasure(): Measure {
  if (canvasCtx === undefined) {
    try {
      canvasCtx = document.createElement("canvas").getContext("2d");
    } catch {
      canvasCtx = null;
    }
  }
  const ctx = canvasCtx;
  if (!ctx) return estimateWidth;
  const family = getComputedStyle(document.body).fontFamily || "sans-serif";
  return (word, size) => {
    ctx.font = `600 ${size}px ${family}`;
    return ctx.measureText(word).width || estimateWidth(word, size);
  };
}

const PAD = 3;

// Classic word-cloud layout: biggest words first, each walking an outward spiral from the
// centre until its box doesn't overlap anything already placed. Words never get clipped
// because the caller sizes the viewBox to the bounds of what was placed.
export function layoutWords(words: WordCount[], measure: Measure = estimateWidth, aspect = 2.2): PlacedWord[] {
  if (words.length === 0) return [];
  const max = words[0].count;
  const min = words[words.length - 1].count;
  const span = Math.max(max - min, 1);
  const placed: PlacedWord[] = [];
  const overlaps = (a: PlacedWord) =>
    placed.some(
      (b) =>
        Math.abs(a.x - b.x) * 2 < a.w + b.w + PAD * 2 && Math.abs(a.y - b.y) * 2 < a.h + b.h + PAD * 2
    );

  for (const word of words) {
    const t = (word.count - min) / span;
    const size = Math.round(13 + Math.sqrt(t) * 25);
    const node: PlacedWord = {
      ...word,
      size,
      x: 0,
      y: 0,
      w: measure(word.word, size),
      h: size * 1.05,
      tier: t > 0.66 ? 0 : t > 0.33 ? 1 : 2,
    };
    for (let i = 0; i < 4000; i++) {
      const angle = i * 0.35;
      const radius = 1.6 * angle;
      node.x = Math.cos(angle) * radius * aspect;
      node.y = Math.sin(angle) * radius;
      if (!overlaps(node)) break;
    }
    placed.push(node);
  }
  return placed;
}

interface WordMapProps {
  comments: string[];
  maxWidth?: number;
}

const WordMap = ({ comments, maxWidth = 640 }: WordMapProps) => {
  const words = useMemo(() => countWords(comments), [comments]);
  const placed = useMemo(() => layoutWords(words, browserMeasure()), [words]);

  if (words.length === 0) {
    return <p className="word-map-empty">No comments to summarize.</p>;
  }

  const margin = 6;
  const left = Math.min(...placed.map((p) => p.x - p.w / 2)) - margin;
  const right = Math.max(...placed.map((p) => p.x + p.w / 2)) + margin;
  const top = Math.min(...placed.map((p) => p.y - p.h / 2)) - margin;
  const bottom = Math.max(...placed.map((p) => p.y + p.h / 2)) + margin;
  const width = right - left;
  const height = bottom - top;

  return (
    <svg
      className="word-map"
      role="img"
      aria-label={`Word map of comments: ${words.slice(0, 5).map((w) => w.word).join(", ")}`}
      viewBox={`${left} ${top} ${width} ${height}`}
      width="100%"
      style={{ maxWidth: Math.min(maxWidth, width * 1.15) }}
    >
      {placed.map((p) => (
        <text
          key={p.word}
          className={`word-map-word tier-${p.tier}`}
          x={p.x}
          y={p.y}
          fontSize={p.size}
          textAnchor="middle"
          dominantBaseline="central"
        >
          <title>{`${p.word}: ${p.count}`}</title>
          {p.word}
        </text>
      ))}
    </svg>
  );
};

export default WordMap;
